/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Direction } from "../trickplay";
import type { TThumbnailEntry } from "../types";
import { compute10FootWindowRatio } from "./ThumbnailManager10FootConfig";
import type {
  PlaybackCategory,
  T10FootThumbnailConfig
} from "./ThumbnailManager10FootConfig";

/**
 * Eviction policy for the 10-foot sliding-window cache, as pure functions.
 *
 * Answers "which entries should go, and in what order" without touching the
 * cache. The manager keeps the effects — revoking object URLs, deleting keys,
 * bumping counters — so the decision can be tested against its edge cases
 * directly rather than inferred from blob-accounting several layers up.
 *
 * The policy is what keeps MAX_CACHE_SIZE a genuine hard ceiling on
 * memory-constrained TVs, where every retained entry pins native memory behind
 * an object URL. Getting it wrong does not fail loudly — it degrades into
 * one-eviction-per-pass while eight fetches land, which is how the cache
 * previously parked at the cap indefinitely.
 */

/** The eviction-protected span, in frame-epoch seconds. */
export interface TProtectedWindow {
  start: number;
  end: number;
}

/**
 * Eviction candidates in order of preference. Each tier is pre-sorted
 * farthest-from-position first, least-recently-accessed on ties.
 */
export interface TEvictionTiers {
  /**
   * Out of window, BEHIND the direction of travel. The ideal victims, and the
   * only set the original implementation considered.
   */
  trailing: TThumbnailEntry[];
  /**
   * Out of window but AHEAD of travel — freshly fetched look-ahead. Evicting
   * these costs an immediate re-fetch, so they go second.
   */
  outOfWindow: TThumbnailEntry[];
  /**
   * In window but outside the nearest-neighbour probe radius. Only reached when
   * the first two are empty, which is precisely the starvation case that used
   * to degrade eviction to one entry per pass.
   */
  anywhere: TThumbnailEntry[];
}

/** Everything the policy needs to know about the current session. */
export interface TEvictionContext {
  config: T10FootThumbnailConfig;
  category: PlaybackCategory;
  /** Current playback position in the frame-epoch domain. */
  currentPosition: number;
  /** Active seek direction, or null during normal playback. */
  direction: Direction | null;
  /** Lower fetch bound (frame-epoch domain). */
  minOffset: number;
  /** Upper fetch bound (frame-epoch domain). */
  maxOffset: number;
  /** Seconds between consecutive thumbnails. */
  thumbnailIntervalSec: number;
  /**
   * The object URL currently on screen. Its entry is never evicted in any
   * tier — revoking it would reintroduce the blank flash that the
   * last-known-good fallback exists to prevent. It becomes evictable again as
   * soon as a newer frame is delivered.
   */
  pinnedBlobUrl: string | null;
}

/**
 * Signed forward bias for the active trick-play direction (PRD §3.2.3):
 * +DELTA forward, −DELTA backward, 0 when not actively seeking. Leans the
 * protected window toward the direction the user is scrubbing so the
 * most-relevant thumbnails survive.
 */
function directionBias(
  config: T10FootThumbnailConfig,
  direction: Direction | null
): number {
  if (direction === Direction.Forward) {
    return config.DIRECTION_BIAS_DELTA;
  }
  if (direction === Direction.Backward) {
    return -config.DIRECTION_BIAS_DELTA;
  }
  return 0;
}

/**
 * Compute the eviction-protected window: a span of slots centred on the current
 * position, split forward/backward by the position ratio and leaned toward the
 * active seek direction (PRD §3.5). A forward scrub keeps more forward
 * thumbnails (and evicts from behind); a backward scrub the reverse.
 *
 * Applies to LIVE and VOD identically — for LIVE the forward side simply has no
 * entries beyond the live edge, so only the denser backward side is actually
 * protected.
 *
 * The window is deliberately NARROWER than MAX_CACHE_SIZE
 * (PROTECTED_WINDOW_RATIO): a window as wide as the cap leaves no
 * out-of-window candidates whenever the cache is well-centred on the playhead,
 * which is exactly what starved eviction before.
 */
export function computeProtectedWindow(
  context: TEvictionContext
): TProtectedWindow {
  // Position ratio relative to the SEEKABLE range (buffer/program start →
  // available/live edge), not the static airing window — so "near edge"
  // (ratio > LIVE_EDGE_RATIO_THRESHOLD) is meaningful for LIVE (DVR-relative)
  // and VOD alike.
  const rangeSize = context.maxOffset - context.minOffset;
  const positionRatio =
    rangeSize > 0
      ? (context.currentPosition - context.minOffset) / rangeSize
      : 0;
  let clampedRatio = positionRatio;
  if (clampedRatio < 0) {
    clampedRatio = 0;
  } else if (clampedRatio > 1) {
    clampedRatio = 1;
  }

  const windowRatio = compute10FootWindowRatio(
    context.category,
    clampedRatio,
    context.config.BASE_FORWARD_RATIO_WEIGHT,
    directionBias(context.config, context.direction)
  );
  const windowSlots = Math.max(
    1,
    Math.round(
      context.config.MAX_CACHE_SIZE * context.config.PROTECTED_WINDOW_RATIO
    )
  );
  const forwardSlots = Math.round(windowSlots * windowRatio.forwardPct);
  const backwardSlots = windowSlots - forwardSlots;
  return {
    start:
      context.currentPosition - backwardSlots * context.thumbnailIntervalSec,
    end: context.currentPosition + forwardSlots * context.thumbnailIntervalSec
  };
}

/**
 * Order candidates farthest-from-position first, least-recently accessed first
 * on ties.
 *
 * Sorting rather than taking the first N in Map iteration order matters because
 * Map order is insertion/fetch order, not time order — and at the extreme
 * clamped ratio the nearest out-of-window entry can sit only ~38 slots behind
 * the playhead.
 *
 * Sorts in place and returns the same array, matching the previous behaviour.
 */
export function sortEvictionCandidates(
  candidates: TThumbnailEntry[],
  currentPosition: number
): TThumbnailEntry[] {
  return candidates.sort((a, b) => {
    const distanceDelta =
      Math.abs(b.time - currentPosition) - Math.abs(a.time - currentPosition);
    if (distanceDelta !== 0) {
      return distanceDelta;
    }
    const aTs = a.lastAccessTimestamp !== undefined ? a.lastAccessTimestamp : 0;
    const bTs = b.lastAccessTimestamp !== undefined ? b.lastAccessTimestamp : 0;
    return aTs - bTs;
  });
}

/**
 * Partition the cache into the three ordered eviction tiers in a single pass.
 *
 * While trick-play is active, out-of-window candidates are split by side.
 * Ranking by |distance| alone is direction-blind: whenever the cached run is
 * forward-heavy relative to the playhead (a backward scrub, or the transient
 * right after preload), the farthest out-of-window entry is the freshest
 * look-ahead entry just fetched. Evicting it means immediately re-fetching it —
 * tolerable one at a time, a visible hole in the look-ahead when a whole batch
 * goes at once.
 *
 * @param entries - Every entry currently in the cache.
 * @param window  - The protected span from computeProtectedWindow().
 * @param context - Current session state.
 */
export function collectEvictionTiers(
  entries: Iterable<TThumbnailEntry>,
  window: TProtectedWindow,
  context: TEvictionContext
): TEvictionTiers {
  const trailing: TThumbnailEntry[] = [];
  const outOfWindow: TThumbnailEntry[] = [];
  const anywhere: TThumbnailEntry[] = [];

  // Frames the nearest-neighbour probe is about to read. Never offered as
  // last-resort candidates, or the displayed frame starts flickering back to
  // the last-known-good fallback.
  const probeRadius =
    context.config.MAX_ACCEPTABLE_GAP * context.thumbnailIntervalSec;

  for (const entry of entries) {
    if (isEntryPinned(entry, context.pinnedBlobUrl)) {
      continue;
    }
    const inWindow = entry.time >= window.start && entry.time <= window.end;
    if (!inWindow) {
      const isTrailing =
        context.direction === Direction.Forward
          ? entry.time < window.start
          : entry.time > window.end;
      if (context.direction === null || isTrailing) {
        trailing.push(entry);
      } else {
        outOfWindow.push(entry);
      }
      continue;
    }
    if (Math.abs(entry.time - context.currentPosition) > probeRadius) {
      anywhere.push(entry);
    }
  }

  return {
    trailing: sortEvictionCandidates(trailing, context.currentPosition),
    outOfWindow: sortEvictionCandidates(outOfWindow, context.currentPosition),
    anywhere: sortEvictionCandidates(anywhere, context.currentPosition)
  };
}

/**
 * True when an entry must never be evicted because its blob is the one
 * currently on screen.
 */
export function isEntryPinned(
  entry: TThumbnailEntry,
  pinnedBlobUrl: string | null
): boolean {
  return entry.imgBlob !== null && entry.imgBlob === pinnedBlobUrl;
}

/**
 * Choose the entries to evict so the cache drops genuinely BELOW the cap.
 *
 * Drains to a target rather than evicting a fixed batch, walking the three
 * tiers in order of preference. The target overshoots the cap by one plus a
 * batch, so the following ~N completions find headroom and skip the scan
 * entirely.
 *
 * The third tier's allowance is computed from the cache size AFTER the first
 * two tiers have been taken, exactly as the original in-place implementation
 * did by re-reading cache.size mid-pass. Each victim removes exactly one entry,
 * so subtracting the running count reproduces that read precisely — but it is
 * the subtle part of this function, and collapsing it into a single up-front
 * target would silently over-evict.
 *
 * @param tiers     - Ordered candidates from collectEvictionTiers().
 * @param cacheSize - Cache size before any eviction.
 * @param cap       - MAX_CACHE_SIZE.
 * @param batchSize - Eviction batch size for the current mode.
 * @returns The entries to evict, in eviction order.
 */
export function selectEvictionVictims(
  tiers: TEvictionTiers,
  cacheSize: number,
  cap: number,
  batchSize: number
): TThumbnailEntry[] {
  // Drain below the cap, not merely to it: the caller's entry guard is
  // `size < cap`, so a target that merely reaches the cap would let the cache
  // park exactly at the ceiling indefinitely.
  const target = cacheSize - cap + 1 + batchSize;
  const victims: TThumbnailEntry[] = [];

  const take = (candidates: TThumbnailEntry[], limit: number): void => {
    const count = Math.min(limit, candidates.length);
    for (let i = 0; i < count; i++) {
      victims.push(candidates[i]);
    }
  };

  take(tiers.trailing, target);
  if (victims.length < target) {
    take(tiers.outOfWindow, target - victims.length);
  }
  // Re-evaluate against the post-eviction size, as the original did.
  const sizeAfterFirstTwoTiers = cacheSize - victims.length;
  if (sizeAfterFirstTwoTiers >= cap) {
    take(tiers.anywhere, sizeAfterFirstTwoTiers - cap + 1);
  }
  return victims;
}
